import { SwUpdate, UnrecoverableStateEvent, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, first, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { BaseParamsService } from '@app/core/base-params.service';

@Injectable()
export class UpdateService implements OnDestroy {
  public updateScheduled$ = new BehaviorSubject(false);
  private onDestroy$ = new Subject<void>();

  constructor(private router: Router, private updates: SwUpdate, private baseParamsService: BaseParamsService) { }

  public register(): void {
    this.registerUpdate<VersionReadyEvent>(
      this.updates.versionUpdates.pipe(
        tap((evt) => this.logVersionUpdateEvent(evt)),
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
        takeUntil(this.onDestroy$),
      ),
    ).subscribe(() => this.handleAvailableUpdate());

    this.registerUpdate<UnrecoverableStateEvent>(
      this.updates.unrecoverable.pipe(
        takeUntil(this.onDestroy$),
      ),
    ).subscribe((e) => this.handleUnrecoverableUpdate(e));
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.complete();
  }

  private async handleAvailableUpdate(): Promise<void> {
    console.log(`[UPDATE] Update is available`);
    const result = await this.updates.activateUpdate();
    if (result) {
      this.handleActivatedUpdate();
    }
  }

  private handleActivatedUpdate(): void {
    console.log(`[UPDATE] Update has been activated`);
    this.updateScheduled$.next(true);
    this.subscribeForForceUpdate();
  }

  private handleUnrecoverableUpdate(e: UnrecoverableStateEvent): void {
    const eventMsg = JSON.stringify(e);

    console.error(`[UPDATE] is unrecoverable: ${eventMsg}`);
    Sentry.captureMessage(`Unrecoverable update! ${eventMsg}`);

    this.updateScheduled$.next(true);
    this.reloadPage();
    this.subscribeForForceUpdate();
  }

  private subscribeForForceUpdate(): void {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      first(),
    ).subscribe(() => this.reloadPage());
  }

  private reloadPage(): void {
    document.location.reload();
  }

  private logVersionUpdateEvent(evt: VersionEvent): void {
    switch (evt.type) {
      case 'VERSION_DETECTED':
        console.log(`[UPDATE] Downloading new app version: ${evt.version.hash}`);
        break;
      case 'VERSION_READY':
        console.log(`[UPDATE] Current app version: ${evt.currentVersion.hash}`);
        console.log(`[UPDATE] New app version ready for use: ${evt.latestVersion.hash}`);
        break;
      case 'VERSION_INSTALLATION_FAILED':
        console.log(`[UPDATE] Failed to install app version '${evt.version.hash}': ${evt.error}`);
        break;
    }
  }

  private registerUpdate<T>(registerStream$: Observable<T>): Observable<T | never> {
    return this.baseParamsService.getParamsResolvedObservable().pipe(
      switchMap(() => this.baseParamsService.isInsideMobileApp() ? EMPTY : registerStream$));
  }
}
